import { useEffect, useLayoutEffect, useState, FC, useMemo } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
} from '@chakra-ui/react';
import { LabelV2Version } from 'types/releases';
import { TableList } from 'app/components/ChakraTableList';
import { useAppDispatch, useAppSelector } from 'app/store';
import { fetchSongEffect } from 'app/store/SongList/effects';
import { getSong } from 'app/store/SongList/selectors';
import { getRows } from './utils';

export interface ReleaseIssuesDialogProps
  extends Omit<ModalProps, 'children' | 'onClose'> {
  version?: LabelV2Version;
  onClose?: () => void;
}

export const ReleaseIssuesDialog: FC<ReleaseIssuesDialogProps> = ({
  version,
  isOpen: isOpenProp,
  onClose = () => {},
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(isOpenProp);

  useLayoutEffect(() => {
    setOpen(isOpenProp);
  }, [isOpenProp]);

  useEffect(() => {
    if (version?.song.guid) {
      dispatch(fetchSongEffect({ guid: version?.song.guid }));
    }
  }, [dispatch, version?.song.guid]);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const song = useAppSelector(getSong);
  const title = 'Data Issues';
  const audioSongTitle = `${version?.title} - ${version?.song.title}`;

  const rows = useMemo(() => getRows(version, song?.status), [
    song?.status,
    version,
  ]);

  return (
    <Modal
      size="lg"
      isOpen={open}
      onClose={handleClose}
      data-testid={`${title} dialog`}
      {...rest}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader data-testid="dialog-title" title={title}>
          {title}
          <Text
            overflow="hidden"
            textOverflow="ellipsis"
            color="M.60"
            title={audioSongTitle}
          >
            {audioSongTitle}
          </Text>
        </ModalHeader>
        <ModalBody data-testid="dialog-content">
          <Text fontSize="xs" mb={4}>
            The following issues needs to be resolved before you can export the
            release. Please go the Song to resolve the issues.
          </Text>
          <TableList
            columns={[
              { key: 'avatar', name: '', width: 44 },
              { key: 'title', name: '' },
              { key: 'issue', name: '' },
              { key: 'link', name: '', width: 100 },
            ]}
            rows={rows}
          />
        </ModalBody>
        <ModalFooter data-testid="dialog-actions">
          <Button
            onClick={handleClose}
            data-testid="confirm-dialog-done-button"
          >
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
